<script>
import { cva } from 'class-variance-authority';

const confirmButtonVariants = cva(
    'px-4 py-2 rounded-lg flex items-center gap-2 text-white hover:opacity-90 transition-opacity',
    {
        variants: {
            type: {
                default: 'bg-nerve hover:bg-nerve-600',
                danger: 'bg-red-500 hover:bg-red-600',
                info: 'bg-blue-500 hover:bg-blue-600',
                success: 'bg-green-500 hover:bg-green-600',
            },
        },
        defaultVariants: {
            type: 'default',
        },
    }
);
</script>

<script setup>
import Loading from '/app/components/NLoading.vue';
import { watch, computed, ref } from 'vue';
import { useClosable } from '/app/composables/closable';
import { useEscapeStack } from '/app/composables/escape-stack';
import { cn } from '/app/utils/helpers';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false
    },
    type: {
        type: String,
        default: 'default',
        validator: (value) => ['default', 'danger', 'info', 'success'].includes(value)
    },
    title: {
        type: String
    },
    message: {
        type: String
    },
    cancelText: {
        type: String,
        default: 'Cancel'
    },
    confirmText: {
        type: String,
        default: 'Continue'
    },
    hideConfirm: Boolean,
    hideCancel: Boolean,
    hideOverlay: Boolean,

    handleConfirm: {
        type: Function,
    },
    handleCancel: {
        type: Function,
    },
});

const emit = defineEmits(['update:modelValue', 'open', 'close', 'confirm', 'cancel']);

const loading = ref(false);
const modelValueRef = computed(() => props.modelValue);
const { isOpen, payload, open, close: closeModal } = useClosable(modelValueRef);

// Watch modelValue prop to sync with isOpen
watch(() => props.modelValue, (value) => {
    if (value && !isOpen.value) {
        open();
    } else if (!value && isOpen.value) {
        closeModal();
    }
});

// Watch isOpen to emit update:modelValue and events
watch(isOpen, (value) => {
    if (value !== props.modelValue) {
        emit('update:modelValue', value);
    }
    if (value) {
        emit('open');
    } else {
        emit('close');
    }
});

const confirm = async () => {
    if (props.handleConfirm) {
        loading.value = true;
        await props.handleConfirm(payload.value);
        loading.value = false;
        closeModal();
    } else {
        emit('confirm', payload.value);
        closeModal();
    }
};

const cancel = async () => {
    if (props.handleCancel) {
        loading.value = true;
        await props.handleCancel(payload.value);
        loading.value = false;
        closeModal();
    } else {
        emit('cancel', payload.value);
        closeModal();
    }
};

useEscapeStack(isOpen, closeModal);

defineExpose({
    open,
    close: closeModal,
});
</script>

<template>
    <div v-if="isOpen" class="modal is-open" @click.self="closeModal">
        <div v-if="!hideOverlay" class="modal__background" @click="closeModal"></div>
        <div class="modal__content" :class="{ 'shadow-active rounded-lg': hideOverlay }" @keyup.esc="closeModal">
            <div class="notify">
                <div class="notify__content text-align--left pl-5 pt-5 pr-5 pb-4">
                    <slot name="title">
                        <h3 class="mb-4">{{ title }}</h3>
                    </slot>
                    <slot name="content" v-bind="payload || {}" >
                        {{ message }}
                    </slot>
                </div>
                <div class="px-3 pb-3 pt-2 flex items-center justify-end space-x-2">
                    <slot name="actions" :payload="payload" :cancel="cancel" :confirm="confirm">
                        <button aria-label="button" v-if="!hideCancel" class="px-4 py-2 rounded-lg flex hover:bg-dust-100" @click.prevent="cancel">
                            {{ cancelText }}
                        </button>
                        <button :disabled="loading" aria-label="button" v-if="!hideConfirm" :class="cn(confirmButtonVariants({ type: props.type }))" @click.prevent="confirm">
                            <Loading v-if="loading" class="w-4 h-4" loadingClass="text-white" active />
                            {{ confirmText }}
                        </button>
                    </slot>
                </div>
            </div>
        </div>
    </div>
</template>
